// frontend/src/pages/DataWorkspacePage/GridEditor.jsx
/**
 * GridEditor — AG Grid wrapper for the Data Workspace.
 *
 * Responsibilities:
 *  - Render paginated data from the proxy service in AG Grid Community
 *  - Detect cell edits and accumulate a local diff list
 *  - Expose a "Commit Panel" button to review and save changes
 *  - Support column sorting (client-side on current page, server-side trigger on sort change)
 */

import React, { useRef, useCallback, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { DynamicIcon } from '../../components/icons.jsx';
import CommitPanel from './CommitPanel.jsx';

// ----- AG Grid dark theme via CSS variable overrides -----------------------
const GRID_STYLE = {
    '--ag-background-color':              '#0a0f1e',
    '--ag-odd-row-background-color':      '#0d1526',
    '--ag-header-background-color':       '#111827',
    '--ag-header-foreground-color':       '#94a3b8',
    '--ag-foreground-color':              '#cbd5e1',
    '--ag-border-color':                  '#1e293b',
    '--ag-row-hover-color':               '#1e2d45',
    '--ag-selected-row-background-color': '#1e3a5f',
    '--ag-font-size':                     '12px',
    '--ag-cell-horizontal-padding':       '12px',
    '--ag-header-height':                 '36px',
    '--ag-row-height':                    '32px',
};

// ----- Column definition builder -------------------------------------------
const buildColDefs = (columns, pkCols = []) =>
    columns.map(colName => ({
        field:       colName,
        headerName:  colName,
        editable:    !pkCols.includes(colName), // PKs are read-only
        sortable:    true,
        resizable:   true,
        minWidth:    80,
        flex:        1,
        // Highlight PK columns
        headerClass: pkCols.includes(colName) ? 'ag-header-pk' : '',
        cellClass:   pkCols.includes(colName) ? 'ag-cell-pk' : '',
        cellStyle:   pkCols.includes(colName) ? { color: '#38bdf8', fontFamily: 'monospace' } : {},
    }));


const GridEditor = ({
    token,
    connectionId,
    tableName,
    schemaName,
    gridData,
    isLoading,
    onPageChange,
}) => {
    const gridRef = useRef(null);

    // Local diffs accumulated before commit
    const [diffs, setDiffs] = useState([]);     // [{ pk_values, original, modified }]
    const [showCommit, setShowCommit] = useState(false);
    const [page, setPage]   = useState(1);

    // -----------------------------------------------------------------------
    // Derive column defs from first data response
    // -----------------------------------------------------------------------
    const colDefs = useMemo(() => {
        if (!gridData?.columns?.length) return [];
        return buildColDefs(gridData.columns);
    }, [gridData?.columns]);

    const rowData = useMemo(() => gridData?.rows || [], [gridData?.rows]);

    // -----------------------------------------------------------------------
    // Cell value changed → accumulate diffs
    // -----------------------------------------------------------------------
    const onCellValueChanged = useCallback((params) => {
        const { data, colDef, oldValue, newValue } = params;
        if (oldValue === newValue) return;

        setDiffs(prev => {
            // Try to find an existing diff for the same row
            const rowKey = JSON.stringify(data);          // simple identity; ideally use PK
            const existingIdx = prev.findIndex(d => JSON.stringify(d._rowSnapshot) === rowKey);

            if (existingIdx >= 0) {
                const updated = [...prev];
                updated[existingIdx] = {
                    ...updated[existingIdx],
                    modified: { ...updated[existingIdx].modified, [colDef.field]: newValue },
                };
                return updated;
            }

            // New diff
            const original = { ...data, [colDef.field]: oldValue };
            return [...prev, {
                _rowSnapshot: { ...data },
                original,
                modified:     { ...data, [colDef.field]: newValue },
            }];
        });

        // Visual feedback — briefly flash the edited cell
        params.api.flashCells({ rowNodes: [params.node], columns: [colDef.field] });
    }, []);

    // -----------------------------------------------------------------------
    // Pagination
    // -----------------------------------------------------------------------
    const totalPages = gridData ? Math.ceil(gridData.total / gridData.page_size) : 0;

    const goToPage = (newPage) => {
        if (newPage < 1 || newPage > totalPages) return;
        setPage(newPage);
        onPageChange(newPage, gridData?.page_size || 200);
    };

    // -----------------------------------------------------------------------
    // Export current page to CSV
    // -----------------------------------------------------------------------
    const exportCSV = () => {
        gridRef.current?.api?.exportDataAsCsv({
            fileName: `${tableName}_page${page}.csv`,
        });
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-500">
                <DynamicIcon name="Loader2" className="w-8 h-8 animate-spin text-blue-500" />
                <p className="text-sm">Loading data...</p>
            </div>
        );
    }

    if (!gridData) return null;

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* ── Toolbar ─────────────────────────────────────────────────── */}
            <div className="flex items-center justify-between px-4 py-2 bg-[#0d1526] border-b border-white/10 shrink-0">
                <div className="flex items-center gap-3">
                    {/* Pagination */}
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                        <button
                            onClick={() => goToPage(page - 1)}
                            disabled={page <= 1}
                            className="p-1 rounded hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <DynamicIcon name="ChevronLeft" className="w-4 h-4" />
                        </button>
                        <span className="px-2">
                            Page <span className="text-slate-200 font-semibold">{page}</span>
                            {' '}/ <span className="text-slate-200 font-semibold">{totalPages || '—'}</span>
                        </span>
                        <button
                            onClick={() => goToPage(page + 1)}
                            disabled={page >= totalPages}
                            className="p-1 rounded hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <DynamicIcon name="ChevronRight" className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Row count */}
                    <span className="text-xs text-slate-600">
                        Showing {rowData.length} of {(gridData.total || 0).toLocaleString()} rows
                    </span>
                </div>

                <div className="flex items-center gap-2">
                    {/* Diff badge */}
                    {diffs.length > 0 && (
                        <button
                            onClick={() => setShowCommit(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 transition-colors animate-pulse"
                        >
                            <DynamicIcon name="AlertCircle" className="w-3.5 h-3.5" />
                            {diffs.length} unsaved {diffs.length === 1 ? 'change' : 'changes'}
                        </button>
                    )}

                    {/* Export CSV */}
                    <button
                        onClick={exportCSV}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 text-slate-400 border border-white/10 hover:bg-white/10 hover:text-slate-200 transition-colors"
                    >
                        <DynamicIcon name="Download" className="w-3.5 h-3.5" />
                        Export CSV
                    </button>
                </div>
            </div>

            {/* ── AG Grid ─────────────────────────────────────────────────── */}
            <div
                className="flex-1 ag-theme-balham"
                style={{ ...GRID_STYLE, width: '100%', height: '100%' }}
            >
                <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={colDefs}
                    defaultColDef={{
                        sortable:  true,
                        resizable: true,
                        filter:    true,
                    }}
                    onCellValueChanged={onCellValueChanged}
                    enableCellChangeFlash
                    suppressRowClickSelection
                    rowSelection="multiple"
                    animateRows
                    // Clipboard support (Ctrl+C / Ctrl+V Excel-like)
                    enableRangeSelection
                    enableRangeHandle
                    copyHeadersToClipboard
                />
            </div>

            {/* ── Commit Panel ─────────────────────────────────────────────── */}
            {showCommit && (
                <CommitPanel
                    token={token}
                    connectionId={connectionId}
                    tableName={tableName}
                    schemaName={schemaName}
                    diffs={diffs}
                    onClose={() => setShowCommit(false)}
                    onApplied={() => {
                        setDiffs([]);
                        setShowCommit(false);
                        onPageChange(page, gridData?.page_size || 200); // Refresh grid
                    }}
                />
            )}
        </div>
    );
};

export default GridEditor;
